import type { ComponentProps, ReactNode } from 'react';
import { Display, mq, StyleSheet } from 'react-native-unistyles';
import Box from '@/components/Unistyles/Box';
import Button from '@/components/Unistyles/Button';
import Icon from '@/components/Unistyles/Icon';
import Text from '@/components/Unistyles/Text';

type ActionButtonBaseProps = Omit<ComponentProps<typeof Button>, 'children'> & {
  collapse?: boolean;
  forceLabel?: boolean;
  iconSize?: ComponentProps<typeof Icon>['size'];
  iconVariant?: ComponentProps<typeof Icon>['variant'];
  labelSize?: ComponentProps<typeof Text>['size'];
  labelVariant?: ComponentProps<typeof Text>['variant'];
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
};

type ActionButtonProps = ActionButtonBaseProps &
  (
    | { icon: ComponentProps<typeof Icon>['name']; label?: string }
    | { icon?: ComponentProps<typeof Icon>['name']; label: string }
  ) & {
    children?: ReactNode;
  };

export function Container({ children }: { children: ReactNode }) {
  return (
    <Box direction="horizontal" space="md" py="md" px="xl">
      {children}
    </Box>
  );
}

export default function ActionButton({
  children,
  collapse = false,
  forceLabel = false,
  icon,
  iconSize = 'xl',
  iconVariant = 'typography',
  label,
  labelSize = 'md',
  labelVariant = 'typography',
  ref,
  shape,
  size = 'md',
  style: extraStyles,
  ...props
}: ActionButtonProps) {
  const shouldCollapse = collapse && !forceLabel;

  styles.useVariants({
    shouldCollapse,
    size,
  });

  return (
    <Button
      ref={ref}
      outline
      shape={shape ?? 'circle'}
      size={size}
      {...props}
      style={[styles.container, extraStyles]}
    >
      {icon ? <Icon variant={iconVariant} size={iconSize} name={icon} /> : null}
      {label ? (
        shouldCollapse ? (
          <Display mq={mq.only.width('lg')}>
            <Text style={styles.label} variant={labelVariant} size={labelSize}>
              {label}
            </Text>
          </Display>
        ) : (
          <Text style={styles.label} variant={labelVariant} size={labelSize}>
            {label}
          </Text>
        )
      ) : null}
      {children}
    </Button>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.space('sm'),
    justifyContent: 'center',
    minHeight: theme.size(9),
    minWidth: theme.size(9),
    variants: {
      shouldCollapse: {
        false: {
          paddingHorizontal: theme.space('md'),
        },
        true: {
          paddingHorizontal: {
            base: 0,
            lg: theme.space('md'),
          },
        },
      },
      size: {
        lg: {
          minHeight: theme.size(10),
        },
        md: {},
        sm: {
          minHeight: theme.size(8),
          minWidth: theme.size(8),
        },
        xl: {
          minHeight: theme.size(12),
        },
        xs: {
          minHeight: theme.size(7),
          minWidth: theme.size(7),
        },
      },
    },
  },
  label: {
    flexShrink: 0,
    fontWeight: theme.fontWeight.semibold,
  },
}));
